/**
 * Shared dropdown styling for the new LMS design language's selector controls (StudentSelector,
 * AttemptSelectorNew). Kept in one place so "same look and feel" between them is structural, not
 * just two copies that happen to match today.
 */

.container {
	display: flex;
	align-items: center;
	gap: 8px;
	/* Overridden per selector — see StudentSelector.module.css */
	width: var(--selector-container-width, 300px);
	max-width: 100%;
	box-sizing: border-box;
	/* The header row wraps on its own schedule, so a viewport media query can't tell how much width
	   this control actually got. Safe to contain: the width above is never content-driven. */
	container-type: inline-size;
	container-name: selectorDropdown;
}

.labelWrapper {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	/* Without min-width: 0 this flex item won't shrink below its content — i.e. below .container's
	   explicit width — so .container's max-width: 100% resolves against a parent sized to fit it and
	   nothing can ever shrink. */
	min-width: 0;
	max-width: 100%;
}

.label {
	display: block;
	padding-left: 0.3rem;
	color: #404040;
	font-size: 14px;
	font-weight: 500;
}

/* Matches CoreFilterButton's variant="button" case for borders (packages/components/src/core/button/FilterButton.tsx) */
.dropdown {
	/* MUI's InputBase root ships its own `width: 100%` base style; !important is required to actually
	   constrain it to the remaining space inside .container's fixed width rather than the select's own default. */
	flex: 1 !important;
	min-width: 0 !important;
	width: auto !important;
	height: 36px !important;
	font-size: 14px !important;
	font-weight: 500 !important;
	color: #333333 !important;
	border: 1px solid #e0e0e0 !important;
	border-radius: 0.7rem !important;
	box-sizing: border-box !important;
}

/**
 * The read-only twin of .dropdown: same box, no control. Values are duplicated rather than composed
 * because .dropdown's are all `!important` overrides aimed at MUI's InputBase, and none of that applies
 * to a plain div — inheriting them would carry the fight without the opponent. Padding is set here
 * because MUI supplied it on the real select; the arrow's 32px of right padding is deliberately not
 * reproduced, since there is no arrow.
 */
.staticValue {
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
	height: 36px;
	padding: 0 14px;
	font-size: 14px;
	font-weight: 500;
	color: #333333;
	border: 1px solid #e0e0e0;
	border-radius: 0.7rem;
	box-sizing: border-box;
}

/* The app's shared MuiOutlinedInput theme override (packages/components/src/theme/index.tsx) draws the
   visible border on this inner fieldset, not on .dropdown's own root — including a separate, more specific
   rule for the focused state. !important is required so our single border on .dropdown wins in every state. */
.dropdown :global(.MuiOutlinedInput-notchedOutline) {
	border: none !important;
}

.dropdown:global(.Mui-focused) {
	outline: 2px solid var(--focus-outline-color-dark);
	outline-offset: 2px;
}

/* .dropdown is a flex row internally (MuiOutlinedInput-root), and .MuiSelect-select (where the closed
   value/renderValue renders) is a flex item in it. Flex items default to min-width: auto, i.e. "never
   shrink below content width" — without overriding that, the closed value box just grows to fit the
   full content instead of constraining it, so OverflowText never sees a real overflow to ellipsis/tooltip. */
.dropdown :global(.MuiSelect-select) {
	min-width: 0;
}

.optionDisplay {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	width: 100%;
}

.valueLabel {
	flex: 1;
	min-width: 0;
}

.valueBadge {
	flex-shrink: 0;
}

/* Below the app's mobile breakpoint (theme.breakpoints.values.sm), there's no hover to reveal a
   truncated value, so let it wrap instead of clipping — the dropdown is allowed to grow taller, but
   items stay centered so the dropdown arrow tracks the (possibly multi-line) content. */
@media (max-width: 767.98px) {
	.dropdown {
		height: auto !important;
		min-height: 40px;
	}

	.staticValue {
		height: auto;
		min-height: 40px;
		padding-top: 6px;
		padding-bottom: 6px;
	}

	.optionDisplay {
		align-items: center;
	}

	.valueLabel:global(.MuiTypography-root) {
		white-space: normal;
		overflow: visible;
		text-overflow: clip;
		word-break: break-word;
	}
}
